import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import FollowButton from '../components/FollowButton';
import TipButton from '../components/TipButton';
import HumanVerifiedBadge from '../components/HumanVerifiedBadge';
import PostComposer from '../components/PostComposer';
import PostCard from '../components/PostCard';
import { useChatPopup } from '../contexts/ChatPopupContext';
import {
  Heart,
  Share2,
  MoreHorizontal,
  Users,
  Eye,
  DollarSign,
  Flag,
  Ban,
  Crown,
  Check,
  Camera,
  MapPin,
  Calendar,
  Link as LinkIcon,
  Twitter,
  Instagram,
  Globe,
  Music,
  Video,
  BookOpen,
  Info,
  Palette,
  MessageSquare,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const UnifiedProfile = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openConversationWithUser, isDesktop } = useChatPopup();
  const location = window.location;
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [following, setFollowing] = useState(false);
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const [stats, setStats] = useState({
    followers: 0,
    following: 0,
    totalViews: 0,
    earnings: 0
  });

  // Handle deep link to specific post (e.g., /u/handle?p=post_id)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const postId = urlParams.get('p');
    
    if (postId && profile) {
      // Wait for posts to render, then scroll to the post
      setTimeout(() => {
        const postElement = document.getElementById(`post-${postId}`);
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedPostId(postId);
          // Remove highlight after 2 seconds
          setTimeout(() => setHighlightedPostId(null), 2000);
        } else {
          toast.error("This post isn't available");
        }
      }, 500);
    }
  }, [location.search, profile]);

  // Determine if current user is viewing their own profile
  // Check multiple ways since user object structure may vary
  const isOwnProfile = React.useMemo(() => {
    if (!user || !profile) return false;
    
    // Method 1: Direct ID comparison
    if (user.id === profile.user_id) return true;
    
    // Method 2: Handle comparison
    if (user.handle && user.handle === profile.handle) return true;
    
    // Method 3: Check if user has creator_profile with matching user_id
    if (user.creator_profile && user.creator_profile.user_id === profile.user_id) return true;
    
    // Method 4: Check email match (if both available)
    if (user.email && profile.email && user.email === profile.email) return true;
    
    return false;
  }, [user, profile]);
  
  // Debug logging - TEMPORARY
  React.useEffect(() => {
    if (user && profile) {
      console.log('🔍 Profile Ownership Debug:', {
        userObject: user,
        profileObject: profile,
        isOwnProfile,
        checks: {
          idMatch: user.id === profile.user_id,
          handleMatch: user.handle === profile.handle,
          creatorProfileMatch: user.creator_profile?.user_id === profile.user_id
        }
      });
    }
  }, [user, profile, isOwnProfile]);

  useEffect(() => {
    fetchProfile();
  }, [handle]);

  // Handle tab switching from URL hash (redirect old 'posts' hash to overview)
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove the #
    if (hash === 'posts') {
      // Redirect old posts hash to overview
      window.location.hash = 'overview';
      setActiveTab('overview');
    } else if (hash && ['overview', 'comics', 'art-gallery', 'novels', 'music', 'live', 'about'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [window.location.hash]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Fetch unified profile by handle
      const response = await axios.get(`${API}/u/${handle}`);
      setProfile(response.data);
      
      // Get stats
      setStats({
        followers: response.data.metrics?.followers || 0,
        following: response.data.metrics?.following || 0,
        totalViews: response.data.metrics?.total_views || 0,
        earnings: response.data.metrics?.earnings || 0
      });
      
      // Check if following
      if (user && user.id !== response.data.user_id) {
        // TODO: Check following status from backend
        setFollowing(false);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response?.status === 404) {
        navigate('/404');
      }
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/u/${handle}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.display_name} (@${profile.handle})`,
          text: profile.bio_richtext || 'Check out this creator on Trap Comix!',
          url: shareUrl
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      toast.success('Profile link copied!');
    }
  };

  const handleMessageClick = async () => {
    if (!user) {
      toast.error('Please sign in to send messages');
      navigate('/auth');
      return;
    }

    try {
      // On desktop, open popup chat
      if (isDesktop) {
        await openConversationWithUser(
          profile.user_id,
          profile.display_name,
          profile.avatar_url
        );
      } else {
        // On mobile, navigate to messages page
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/messages/conversation/create`,
          { recipient_id: profile.user_id },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        navigate(`/messages?conversation=${response.data.id}`);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    }
  };

  const getTierBadge = (tier) => {
    const tiers = {
      1: { name: 'Rookie', color: 'bg-gray-500' },
      2: { name: 'Rising Star', color: 'bg-blue-500' },
      3: { name: 'Featured Creator', color: 'bg-purple-500' },
      4: { name: 'Trap Star', color: 'bg-orange-500' }
    };
    return tiers[tier] || null;
  };

  // Get visible tabs based on profile flags and content
  const getVisibleTabs = () => {
    if (!profile) return ['overview'];
    
    const tabs = ['overview'];
    
    // Posts tab removed - all posts now show on Overview
    
    if (profile.active_tabs?.includes('comics') || profile.is_creator) {
      tabs.push('comics');
    }
    
    if (profile.active_tabs?.includes('art-gallery') || profile.metrics?.art_count > 0) {
      tabs.push('art-gallery');
    }
    
    if (profile.active_tabs?.includes('novels')) {
      tabs.push('novels');
    }
    
    if (profile.active_tabs?.includes('music') || profile.is_musician) {
      tabs.push('music');
    }
    
    if (profile.active_tabs?.includes('live') || profile.is_streamer) {
      tabs.push('live');
    }
    
    tabs.push('about');
    
    return tabs;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Profile Not Found</h2>
          <p className="text-gray-400 mb-6">The profile @{handle} doesn't exist</p>
          <Button onClick={() => navigate('/comics')}>Discover Creators</Button>
        </div>
      </div>
    );
  }

  const visibleTabs = getVisibleTabs();
  const tierBadge = profile.creator_tier ? getTierBadge(profile.creator_tier) : null;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header / Hero Section */}
      <div className="relative">
        {/* Banner */}
        <div 
          className="w-full h-64 bg-gradient-to-r from-orange-900/20 via-red-900/20 to-pink-900/20 bg-cover bg-center"
          style={{
            backgroundImage: profile.banner_url ? `url(${BACKEND_URL}${profile.banner_url})` : undefined
          }}
        >
          {isOwnProfile && (
            <button className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors">
              <Camera className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Profile Info */}
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
          <div className="relative -mt-20 pb-6">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
              {/* Avatar */}
              <div className="relative">
                <div className="w-40 h-40 rounded-full border-4 border-black bg-gray-800 overflow-hidden">
                  {profile.avatar_url ? (
                    <img 
                      src={profile.avatar_url.startsWith('http') ? profile.avatar_url : `${BACKEND_URL}${profile.avatar_url}`}
                      alt={profile.display_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-gray-400">
                      {profile.display_name[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
                {isOwnProfile && (
                  <button className="absolute bottom-0 right-0 bg-orange-500 hover:bg-orange-600 rounded-full p-2 transition-colors">
                    <Camera className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Name, Handle, Badges */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-black mb-1">{profile.display_name}</h1>
                    <p className="text-gray-400 text-lg mb-3">@{profile.handle}</p>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profile.human_verified && (
                        <HumanVerifiedBadge size="default" />
                      )}
                      {tierBadge && (
                        <Badge className={`${tierBadge.color} text-white flex items-center gap-1`}>
                          <Crown className="w-3 h-3" />
                          {tierBadge.name}
                        </Badge>
                      )}
                      {profile.badges?.includes('verified') && (
                        <Badge className="bg-blue-500 text-white flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Verified
                        </Badge>
                      )}
                      {profile.founder_flags?.trap_original && (
                        <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                          Trap Original
                        </Badge>
                      )}
                    </div>

                    {/* Bio */}
                    {profile.bio_richtext && (
                      <p className="text-gray-300 max-w-2xl mb-4">{profile.bio_richtext}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {isOwnProfile && (
                      <Button
                        onClick={() => navigate('/profile/editor')}
                        className="bg-gray-700 hover:bg-gray-600"
                      >
                        Customize Profile
                      </Button>
                    )}
                    
                    {/* Follow Button */}
                    {!isOwnProfile && (
                      <>
                        <FollowButton 
                          targetUserId={profile.user_id} 
                          targetHandle={profile.handle}
                          size="default"
                        />
                        <Button 
                          onClick={handleMessageClick}
                          variant="outline" 
                          className="border-gray-700 hover:bg-gray-800"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Message
                        </Button>
                        <TipButton 
                          creatorId={profile.user_id}
                          creatorHandle={profile.handle}
                        />
                      </>
                    )}
                    
                    <Button onClick={handleShare} variant="ghost" size="icon">
                      <Share2 className="w-5 h-5" />
                    </Button>
                    
                    {!isOwnProfile && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-gray-900 border-gray-700">
                          <DropdownMenuItem className="text-gray-300 hover:text-white cursor-pointer">
                            <Flag className="w-4 h-4 mr-2" />
                            Report Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-500 hover:text-red-400 cursor-pointer">
                            <Ban className="w-4 h-4 mr-2" />
                            Block User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Stats Row */}
                <div className="flex items-center gap-6 text-sm mt-4">
                  <Link to={`/u/${handle}/follow`} className="hover:text-orange-500 transition-colors">
                    <span className="font-bold text-white">{stats.followers.toLocaleString()}</span>
                    <span className="text-gray-400 ml-1">Followers</span>
                  </Link>
                  <Link to={`/u/${handle}/follow`} className="hover:text-orange-500 transition-colors">
                    <span className="font-bold text-white">{stats.following.toLocaleString()}</span>
                    <span className="text-gray-400 ml-1">Following</span>
                  </Link>
                  <div>
                    <span className="font-bold text-white">{stats.totalViews.toLocaleString()}</span>
                    <span className="text-gray-400 ml-1">Total Views</span>
                  </div>
                  {isOwnProfile && stats.earnings > 0 && (
                    <div>
                      <span className="font-bold text-orange-500">${stats.earnings.toLocaleString()}</span>
                      <span className="text-gray-400 ml-1">Earnings</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-800 overflow-x-auto">
            <div className="flex space-x-8">
              {visibleTabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-4 px-2 font-medium transition-colors capitalize whitespace-nowrap ${
                    activeTab === tab
                      ? 'text-orange-500 border-b-2 border-orange-500'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {tab === 'comics' && <BookOpen className="w-4 h-4 inline mr-2" />}
                  {tab === 'art-gallery' && <Palette className="w-4 h-4 inline mr-2" />}
                  {tab === 'music' && <Music className="w-4 h-4 inline mr-2" />}
                  {tab === 'live' && <Video className="w-4 h-4 inline mr-2" />}
                  {tab === 'about' && <Info className="w-4 h-4 inline mr-2" />}
                  {tab.replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">
        {activeTab === 'overview' && <OverviewTab profile={profile} isOwnProfile={isOwnProfile} setActiveTab={setActiveTab} highlightedPostId={highlightedPostId} />}
        {activeTab === 'comics' && <ComicsTab profile={profile} isOwnProfile={isOwnProfile} />}
        {activeTab === 'art-gallery' && <ArtGalleryTab profile={profile} isOwnProfile={isOwnProfile} />}
        {activeTab === 'novels' && <NovelsTab profile={profile} isOwnProfile={isOwnProfile} />}
        {activeTab === 'music' && <MusicTab profile={profile} isOwnProfile={isOwnProfile} />}
        {activeTab === 'live' && <LiveTab profile={profile} isOwnProfile={isOwnProfile} />}
        {activeTab === 'about' && <AboutTab profile={profile} isOwnProfile={isOwnProfile} />}
      </div>
    </div>
  );
};

// Tab Components (Placeholders for now)
const OverviewTab = ({ profile, isOwnProfile, setActiveTab, highlightedPostId }) => {
  const { user } = useAuth();
  const [featuredSeries, setFeaturedSeries] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [copyingLatest, setCopyingLatest] = useState(false);

  useEffect(() => {
    fetchOverviewData();
    fetchRecentPosts();
  }, [profile.user_id]);
  
  const handleCopyLatestPost = async () => {
    if (recentPosts.length === 0) return;
    
    setCopyingLatest(true);
    try {
      const latestPost = recentPosts[0];
      const url = `${window.location.origin}/u/${profile.handle}?p=${latestPost.id}`;
      await navigator.clipboard.writeText(url);
      toast.success('Link to latest post copied!');
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy link');
    } finally {
      setCopyingLatest(false);
    }
  };

  const fetchRecentPosts = async () => {
    try {
      setPostsLoading(true);
      const response = await axios.get(
        `/api/creator/${profile.user_id}/posts?skip=0&limit=5`
      );
      setRecentPosts(response.data || []);
    } catch (error) {
      console.error('Error fetching recent posts:', error);
      setRecentPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  const handlePostCreated = (newPost) => {
    // Add new post to the top of the list
    setRecentPosts([newPost, ...recentPosts]);
  };

  const handlePostUpdated = (updatedPost) => {
    setRecentPosts(recentPosts.map(p => p.id === updatedPost.id ? updatedPost : p));
  };

  const handlePostDeleted = (postId) => {
    setRecentPosts(recentPosts.filter(p => p.id !== postId));
  };

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      
      // Fetch featured series (from featured_series_ids or top 3 by popularity)
      const seriesResponse = await axios.get(`${API}/series?creator_id=${profile.user_id}`);
      const allSeries = seriesResponse.data;
      
      // Get featured series if IDs are set
      let featured = [];
      if (profile.featured_series_ids?.length > 0) {
        featured = allSeries.filter(s => profile.featured_series_ids.includes(s.id));
      } else {
        // Fallback: top 3 most popular series
        featured = allSeries
          .sort((a, b) => (b.metrics_cache?.views || 0) - (a.metrics_cache?.views || 0))
          .slice(0, 3);
      }
      setFeaturedSeries(featured);

      // Fetch recent episodes/updates
      // TODO: Add endpoint to get recent episodes across all series
      // For now, show series with latest updates
      const recentSeries = allSeries
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 5);
      setRecentUpdates(recentSeries);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching overview data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Creator Post Composer - FB style, directly on Overview */}
      {user && (user.role === 'artist' || user.role === 'author' || user.role === 'admin') && 
       (profile.role === 'artist' || profile.role === 'author' || profile.role === 'admin') && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Share an Update</h2>
          <PostComposer onPostCreated={handlePostCreated} />
        </div>
      )}

      {/* Recent Posts from Creator */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Recent Posts</h2>
          {recentPosts.length > 0 && (
            <Button
              onClick={handleCopyLatestPost}
              disabled={copyingLatest}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Copy Link to Latest
            </Button>
          )}
        </div>
        
        {postsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-400"></div>
          </div>
        ) : recentPosts.length > 0 ? (
          <div className="space-y-4">
            {recentPosts.map(post => (
              <PostCard 
                key={post.id} 
                post={post}
                profileHandle={profile.handle}
                onUpdate={handlePostUpdated}
                onDelete={handlePostDeleted}
                isHighlighted={highlightedPostId === post.id}
              />
            ))}
          </div>
        ) : (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">
                  {isOwnProfile 
                    ? 'Share your first update with your followers!' 
                    : 'No posts yet from this creator'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Updates */}
      {recentUpdates.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Recent Updates</h2>
          <div className="space-y-4">
            {recentUpdates.map(series => (
              <Link key={series.id} to={`/series/${series.id}`}>
                <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        {series.cover_url ? (
                          <img
                            src={series.cover_url.startsWith('http') ? series.cover_url : `${BACKEND_URL}${series.cover_url}`}
                            alt={series.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <BookOpen className="w-8 h-8 text-gray-600" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg mb-1 hover:text-orange-500 transition-colors">
                          {series.title}
                        </h3>
                        <p className="text-sm text-gray-400 mb-2">
                          Updated {new Date(series.updated_at).toLocaleDateString()}
                        </p>
                        <div className="flex items-center gap-2">
                          {series.genres?.slice(0, 3).map(genre => (
                            <Badge key={genre} variant="secondary" className="bg-gray-800 text-gray-300">
                              {genre}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex flex-col items-end gap-1 text-sm text-gray-400">
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{(series.metrics_cache?.views || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          <span>{(series.metrics_cache?.subscribers || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Featured Section - Showcasing other comics at the end */}
      {featuredSeries.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Featured Comics</h2>
            {isOwnProfile && (
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                Manage Featured
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredSeries.map(series => (
              <Link key={series.id} to={`/series/${series.id}`}>
                <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all overflow-hidden group">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    {series.cover_url ? (
                      <img
                        src={series.cover_url.startsWith('http') ? series.cover_url : `${BACKEND_URL}${series.cover_url}`}
                        alt={series.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-bold text-lg text-white mb-1">{series.title}</h3>
                      <p className="text-sm text-gray-300 line-clamp-2">{series.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          <span>{(series.metrics_cache?.views || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          <span>{(series.metrics_cache?.subscribers || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Empty State for new profiles */}
      {featuredSeries.length === 0 && recentUpdates.length === 0 && (
        <div className="text-center py-20">
          <Palette className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No content yet</h3>
          {isOwnProfile ? (
            <>
              <p className="text-gray-400 mb-6">Start your creative journey on Trap Comix!</p>
              <Button
                onClick={() => window.location.href = '/create-series'}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Palette className="w-4 h-4 mr-2" />
                Create Your First Series
              </Button>
            </>
          ) : (
            <p className="text-gray-400">This creator is just getting started. Check back soon!</p>
          )}
        </div>
      )}
    </div>
  );
};

const ComicsTab = ({ profile, isOwnProfile }) => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  useEffect(() => {
    fetchSeries();
  }, [profile.user_id]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/series?creator_id=${profile.user_id}`);
      setSeries(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching series:', error);
      setLoading(false);
    }
  };

  // Filter series
  const filteredSeries = series.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'ongoing') return s.status === 'ongoing';
    if (filter === 'completed') return s.status === 'completed';
    if (filter === 'mature') return s.content_warnings?.length > 0;
    return true;
  });

  // Sort series
  const sortedSeries = [...filteredSeries].sort((a, b) => {
    if (sortBy === 'popular') {
      return (b.metrics_cache?.views || 0) - (a.metrics_cache?.views || 0);
    } else if (sortBy === 'newest') {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === 'a-z') {
      return a.title.localeCompare(b.title);
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Filter and Sort */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Comics & Series</h2>
          <p className="text-gray-400 text-sm mt-1">{sortedSeries.length} series</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter Chips */}
          <div className="flex items-center gap-2">
            {['all', 'ongoing', 'completed', 'mature'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                  filter === f
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-gray-800 text-white border border-gray-700 rounded-lg px-4 py-2 text-sm"
          >
            <option value="popular">Most Popular</option>
            <option value="newest">Newest</option>
            <option value="a-z">A-Z</option>
          </select>
        </div>
      </div>

      {/* Series Grid */}
      {sortedSeries.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No series yet</h3>
          {isOwnProfile ? (
            <>
              <p className="text-gray-400 mb-6">Start creating your first series!</p>
              <Button
                onClick={() => window.location.href = '/create-series'}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Create Series
              </Button>
            </>
          ) : (
            <p className="text-gray-400">This creator hasn't published any series yet.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {sortedSeries.map(s => (
            <Link
              key={s.id}
              to={`/series/${s.id}`}
              className="group"
            >
              <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all duration-300 overflow-hidden">
                {/* Cover Image */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  {s.cover_url ? (
                    <img
                      src={s.cover_url.startsWith('http') ? s.cover_url : `${BACKEND_URL}${s.cover_url}`}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 right-2">
                    <Badge className={s.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}>
                      {s.status}
                    </Badge>
                  </div>

                  {/* Mature Badge */}
                  {s.content_warnings?.length > 0 && (
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-red-500">18+</Badge>
                    </div>
                  )}
                </div>

                {/* Info */}
                <CardContent className="p-3">
                  <h3 className="font-bold text-sm mb-1 line-clamp-2 group-hover:text-orange-500 transition-colors">
                    {s.title}
                  </h3>
                  
                  {/* Genres */}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {s.genres?.slice(0, 2).map(genre => (
                      <span key={genre} className="text-xs text-gray-400">
                        {genre}
                      </span>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      <span>{(s.metrics_cache?.views || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Heart className="w-3 h-3" />
                      <span>{(s.metrics_cache?.subscribers || 0).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Latest Episode */}
                  {s.metrics_cache?.total_chapters > 0 && (
                    <p className="text-xs text-gray-400 mt-2">
                      {s.metrics_cache.total_chapters} episodes
                    </p>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const ArtGalleryTab = ({ profile, isOwnProfile }) => {
  const [artPosts, setArtPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchArtPosts();
  }, [profile.user_id]);

  const fetchArtPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/art/user/${profile.user_id}`);
      setArtPosts(response.data.posts || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching art posts:', error);
      setLoading(false);
    }
  };

  // Filter art posts
  const filteredPosts = artPosts.filter(post => {
    if (filter === 'all') return true;
    return post.category === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Art Gallery</h2>
          <p className="text-gray-400 text-sm mt-1">{filteredPosts.length} artworks</p>
        </div>
        
        {isOwnProfile && (
          <Link to="/upload-artwork">
            <Button className="bg-orange-500 hover:bg-orange-600">
              <Palette className="w-4 h-4 mr-2" />
              Upload Artwork
            </Button>
          </Link>
        )}
      </div>

      {/* Filter Buttons */}
      {artPosts.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {['all', 'digital-art', 'character-design', 'concept-art', 'portrait', 'illustration'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize whitespace-nowrap ${
                filter === f
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {f.replace('-', ' ')}
            </button>
          ))}
        </div>
      )}

      {/* Art Grid */}
      {filteredPosts.length === 0 ? (
        <div className="text-center py-20">
          <Palette className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No artwork yet</h3>
          {isOwnProfile ? (
            <>
              <p className="text-gray-400 mb-6">Start building your digital portfolio!</p>
              <Link to="/upload-artwork">
                <Button className="bg-orange-500 hover:bg-orange-600">
                  <Palette className="w-4 h-4 mr-2" />
                  Upload Your First Artwork
                </Button>
              </Link>
            </>
          ) : (
            <p className="text-gray-400">This artist hasn't uploaded any artwork yet.</p>
          )}
        </div>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {filteredPosts.map(post => (
            <Link
              key={post.id}
              to={`/art/${post.id}`}
              className="break-inside-avoid block group"
            >
              <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all overflow-hidden">
                {/* Image */}
                <div className="relative overflow-hidden">
                  {post.images && post.images[0] ? (
                    <img
                      src={post.images[0].url.startsWith('http') ? post.images[0].url : `${BACKEND_URL}${post.images[0].url}`}
                      alt={post.title}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="aspect-square bg-gray-800 flex items-center justify-center">
                      <Palette className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-center px-4">
                      <h4 className="font-bold text-lg mb-2">{post.title}</h4>
                      <div className="flex items-center justify-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{post.metrics?.views || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          <span>{post.metrics?.likes || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Image Set Badge */}
                  {post.type === 'set' && post.images.length > 1 && (
                    <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {post.images.length} images
                    </div>
                  )}

                  {/* Mature Badge */}
                  {post.settings?.mature_content && (
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-red-500">18+</Badge>
                    </div>
                  )}
                </div>

                {/* Info */}
                <CardContent className="p-3">
                  <h4 className="font-semibold truncate mb-1 group-hover:text-orange-500 transition-colors">
                    {post.title}
                  </h4>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span className="capitalize">{post.metadata?.medium || 'Digital'}</span>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        <span>{post.metrics?.views || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        <span>{post.metrics?.likes || 0}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const NovelsTab = ({ profile, isOwnProfile }) => (
  <div className="space-y-8">
    <h2 className="text-2xl font-bold">Novels</h2>
    <p className="text-gray-400">Novel listings coming soon...</p>
  </div>
);

const MusicTab = ({ profile, isOwnProfile }) => {
  const [tracks, setTracks] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    fetchMusicData();
  }, [profile.user_id]);

  const fetchMusicData = async () => {
    try {
      setLoading(true);
      
      // Fetch user's tracks
      const tracksResponse = await axios.get(`${API}/music/user/${profile.user_id}/tracks`);
      setTracks(tracksResponse.data.tracks || []);
      
      // Fetch user's playlists
      try {
        const playlistsResponse = await axios.get(`${API}/music/user/${profile.user_id}/playlists`);
        setPlaylists(playlistsResponse.data.playlists || []);
      } catch (err) {
        console.log('No playlists yet');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching music data:', error);
      setLoading(false);
    }
  };

  const handlePlayTrack = (track) => {
    if (currentTrack?.id === track.id && isPlaying) {
      setIsPlaying(false);
    } else {
      setCurrentTrack(track);
      setIsPlaying(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Music</h2>
          <p className="text-gray-400 text-sm mt-1">
            {tracks.length} {tracks.length === 1 ? 'track' : 'tracks'}
            {playlists.length > 0 && ` • ${playlists.length} ${playlists.length === 1 ? 'playlist' : 'playlists'}`}
          </p>
        </div>
        
        {isOwnProfile && (
          <Link to="/upload-music">
            <Button className="bg-orange-500 hover:bg-orange-600">
              <Music className="w-4 h-4 mr-2" />
              Upload Track
            </Button>
          </Link>
        )}
      </div>

      {/* Sticky Music Player */}
      {currentTrack && (
        <Card className="bg-gradient-to-r from-orange-900/20 to-red-900/20 border-orange-500/50 sticky top-20 z-40">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              {/* Album Art */}
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                {currentTrack.cover_url ? (
                  <img 
                    src={currentTrack.cover_url.startsWith('http') ? currentTrack.cover_url : `${BACKEND_URL}${currentTrack.cover_url}`}
                    alt={currentTrack.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Music className="w-6 h-6 text-gray-600" />
                  </div>
                )}
              </div>

              {/* Track Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-bold truncate">{currentTrack.title}</h4>
                <p className="text-sm text-gray-400 truncate">{profile.display_name}</p>
              </div>

              {/* Play/Pause Button */}
              <Button
                onClick={() => setIsPlaying(!isPlaying)}
                size="icon"
                className="bg-orange-500 hover:bg-orange-600 rounded-full w-12 h-12"
              >
                {isPlaying ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </Button>

              {/* Stats */}
              <div className="hidden md:flex items-center gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{currentTrack.plays || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  <span>{currentTrack.likes || 0}</span>
                </div>
              </div>
            </div>

            {/* Waveform placeholder - will integrate wavesurfer.js here */}
            <div className="mt-4 h-16 bg-gray-800 rounded-lg flex items-center justify-center">
              <div className="flex gap-1 h-full items-center">
                {Array.from({ length: 50 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-orange-500 rounded-full"
                    style={{ height: `${Math.random() * 100}%` }}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Track Listings */}
      {tracks.length === 0 ? (
        <div className="text-center py-20">
          <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No tracks yet</h3>
          {isOwnProfile ? (
            <>
              <p className="text-gray-400 mb-6">Upload your first track to start building your music portfolio!</p>
              <Link to="/upload-music">
                <Button className="bg-orange-500 hover:bg-orange-600">
                  <Music className="w-4 h-4 mr-2" />
                  Upload Your First Track
                </Button>
              </Link>
            </>
          ) : (
            <p className="text-gray-400">This artist hasn't uploaded any tracks yet.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {tracks.map((track, index) => (
            <Card 
              key={track.id}
              className={`bg-gray-900 border-gray-800 hover:border-orange-500 transition-all cursor-pointer ${
                currentTrack?.id === track.id ? 'border-orange-500 bg-orange-500/5' : ''
              }`}
              onClick={() => handlePlayTrack(track)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Track Number / Play Button */}
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    {currentTrack?.id === track.id && isPlaying ? (
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                        </svg>
                      </div>
                    ) : (
                      <div className="w-8 h-8 hover:bg-gray-800 rounded-full flex items-center justify-center group">
                        <span className="text-gray-400 group-hover:hidden">{index + 1}</span>
                        <svg className="w-4 h-4 text-white hidden group-hover:block" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Album Art */}
                  <div className="w-12 h-12 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                    {track.cover_url ? (
                      <img 
                        src={track.cover_url.startsWith('http') ? track.cover_url : `${BACKEND_URL}${track.cover_url}`}
                        alt={track.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-5 h-5 text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate hover:text-orange-500 transition-colors">
                      {track.title}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span>{track.genre || 'Hip-Hop'}</span>
                      {track.is_explicit && (
                        <Badge className="bg-red-500 text-white text-xs">E</Badge>
                      )}
                      <span>•</span>
                      <span>{track.duration || '3:45'}</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="hidden md:flex items-center gap-6 text-sm text-gray-400">
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      <span>{track.plays || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      <span>{track.likes || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      <span>${track.tips || 0}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-gray-900 border-gray-700">
                      <DropdownMenuItem className="text-gray-300 hover:text-white cursor-pointer">
                        <Heart className="w-4 h-4 mr-2" />
                        Like
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-gray-300 hover:text-white cursor-pointer">
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-gray-300 hover:text-white cursor-pointer">
                        <DollarSign className="w-4 h-4 mr-2" />
                        Tip Artist
                      </DropdownMenuItem>
                      {isOwnProfile && (
                        <>
                          <DropdownMenuItem className="text-gray-300 hover:text-white cursor-pointer">
                            Edit Track
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-500 hover:text-red-400 cursor-pointer">
                            Delete Track
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Playlists Section */}
      {playlists.length > 0 && (
        <div className="mt-12">
          <h3 className="text-xl font-bold mb-4">Playlists</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {playlists.map(playlist => (
              <Card key={playlist.id} className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all cursor-pointer">
                <div className="aspect-square bg-gray-800 flex items-center justify-center">
                  <Music className="w-12 h-12 text-gray-600" />
                </div>
                <CardContent className="p-3">
                  <h4 className="font-semibold truncate">{playlist.title}</h4>
                  <p className="text-sm text-gray-400">{playlist.track_count || 0} tracks</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PostsTab = ({ profile, isOwnProfile }) => {
  const { user } = useAuth();
  const { handle } = useParams(); // Get handle from URL
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Check if current user is the profile owner by comparing handles or user_id
  const canPost = user && profile && (
    isOwnProfile ||  // Use existing check
    (user.handle && user.handle === handle) ||  // Compare logged-in user's handle with URL handle
    (user.creator_profile && user.creator_profile.handle === handle) || // Check creator_profile handle
    user.id === profile.user_id // Direct ID match
  );

  const loadPosts = async (pageNum = 1) => {
    setLoading(true);
    try {
      const skip = (pageNum - 1) * 20;
      
      console.log('📡 Loading posts:', {
        creator_id: profile.user_id,
        skip,
        limit: 20
      });
      
      const response = await axios.get(
        `/api/creator/${profile.user_id}/posts?skip=${skip}&limit=20`
      );
      
      console.log('📥 Posts received:', {
        count: response.data?.length || 0,
        data: response.data
      });
      
      if (pageNum === 1) {
        setPosts(response.data);
      } else {
        setPosts([...posts, ...response.data]);
      }
      
      setHasMore(response.data.length === 20);
    } catch (error) {
      console.error('❌ Error loading posts:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts(1);
  }, [profile.user_id]);

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(posts.map(p => p.id === updatedPost.id ? updatedPost : p));
  };

  const handlePostDeleted = (postId) => {
    setPosts(posts.filter(p => p.id !== postId));
  };

  const handleLoadMore = () => {
    loadPosts(page + 1);
    setPage(page + 1);
  };

  return (
    <div className="space-y-6">
      {/* Composer - for profile owner */}
      {canPost && (
        <PostComposer onPostCreated={handlePostCreated} />
      )}

      {/* Posts Feed */}
      {loading && page === 1 ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No posts yet</h3>
          <p className="text-gray-400">
            {isOwnProfile ? 'Share your first update with your followers!' : 'Check back later for updates from this creator.'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-6">
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                onUpdate={handlePostUpdated}
                onDelete={handlePostDeleted}
              />
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center pt-4">
              <Button
                onClick={handleLoadMore}
                disabled={loading}
                variant="outline"
                className="bg-gray-800 border-gray-700 hover:bg-gray-700"
              >
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const LiveTab = ({ profile, isOwnProfile }) => (
  <div className="space-y-8">
    <h2 className="text-2xl font-bold">Live Streams</h2>
    <p className="text-gray-400">Live streaming and VOD coming soon...</p>
  </div>
);

const AboutTab = ({ profile, isOwnProfile }) => (
  <div className="space-y-8">
    <h2 className="text-2xl font-bold">About</h2>
    <div className="bg-gray-900 rounded-lg p-6">
      <p className="text-gray-300">{profile.bio_richtext || 'No bio yet.'}</p>
      
      {profile.links && Object.keys(profile.links).length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">Links</h3>
          <div className="space-y-2">
            {Object.entries(profile.links).map(([key, url]) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-500 hover:text-orange-400"
              >
                <LinkIcon className="w-4 h-4" />
                {key}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

export default UnifiedProfile;
